/**
 * theme.js
 * Globale Basis: Utilities, Drawer, Modal, Dropdown-Navigation,
 * Quantity Input und Theme-Editor-Integration.
 *
 * Architektur: Web Components (Custom Elements). Dadurch initialisiert sich
 * jede Komponente selbst – auch wenn Sections im Theme Editor oder über die
 * Section Rendering API dynamisch neu gerendert werden.
 */

/* --------------------------------------------------------------------------
 * Utilities
 * ------------------------------------------------------------------------ */

window.theme = window.theme || {};

theme.utils = {
  debounce(fn, wait = 200) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  focusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  },

  trapFocus(container) {
    const elements = theme.utils.focusableElements(container);
    if (elements.length === 0) return;

    const first = elements[0];
    const last = elements[elements.length - 1];

    container._focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', container._focusTrapHandler);
    first.focus();
  },

  removeTrapFocus(container) {
    if (container && container._focusTrapHandler) {
      container.removeEventListener('keydown', container._focusTrapHandler);
      delete container._focusTrapHandler;
    }
  },
};

/* --------------------------------------------------------------------------
 * Header-Höhe als CSS-Variable
 *
 * Misst Ankündigungsleiste und Header und schreibt die Summe als
 * --header-total-height auf das Root-Element. Der Vollbild-Hero nutzt das,
 * um beim Laden exakt den restlichen Viewport zu füllen.
 * ------------------------------------------------------------------------ */

(function () {
  let observer;

  const update = () => {
    let total = 0;
    document.querySelectorAll('.announcement-bar, .header-wrapper').forEach((el) => {
      total += el.offsetHeight;
    });
    document.documentElement.style.setProperty('--header-total-height', `${total}px`);
  };

  const observe = () => {
    update();
    if ('ResizeObserver' in window) {
      observer?.disconnect();
      observer = new ResizeObserver(update);
      document
        .querySelectorAll('.announcement-bar, .header-wrapper')
        .forEach((el) => observer.observe(el));
    } else {
      window.addEventListener('resize', theme.utils.debounce(update, 150));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  // Theme Editor rendert Header-Sections neu, danach neu vermessen.
  document.addEventListener('shopify:section:load', observe);
})();

/* --------------------------------------------------------------------------
 * Drawer (Basis für Menu Drawer und Cart Drawer)
 *
 * Markup-Vertrag:
 * <drawer-component id="...">
 *   <div class="drawer__overlay" data-drawer-close></div>
 *   <div class="drawer__panel">...<button data-drawer-close>...</button></div>
 * </drawer-component>
 * Öffnen über <button data-drawer-open aria-controls="ID">.
 * ------------------------------------------------------------------------ */

class DrawerComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });

    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  open(opener) {
    this.opener = opener || this.opener;
    this.classList.add('is-open');
    this.opener?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    const panel = this.querySelector('.drawer__panel');
    if (panel) theme.utils.trapFocus(panel);
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    theme.utils.removeTrapFocus(this.querySelector('.drawer__panel'));
    if (this.opener) {
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
    }
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}

// Globale Öffner: funktionieren auch für dynamisch gerenderte Buttons.
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;

  const drawer = document.getElementById(opener.getAttribute('aria-controls'));
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open(opener);
  }
});

/* --------------------------------------------------------------------------
 * Dropdown-Navigation (details/summary im Header)
 * Schließt bei Klick außerhalb, Escape und Fokusverlust.
 * ------------------------------------------------------------------------ */

class DetailsDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    if (!this.details || !this.summary) return;

    this.summary.setAttribute('role', 'button');
    this.syncAria();

    this.details.addEventListener('toggle', () => this.syncAria());

    this.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.details.open = false;
      });
    });

    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.details.open) {
        this.details.open = false;
        this.summary.focus();
      }
    });

    document.addEventListener('click', (event) => {
      if (this.details.open && !this.contains(event.target)) {
        this.details.open = false;
      }
    });
  }

  syncAria() {
    this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
  }
}

if (!customElements.get('details-disclosure')) {
  customElements.define('details-disclosure', DetailsDisclosure);
}

/* Desktop mit Maus: Header-Dropdowns bereits beim Hovern öffnen.
 * Delegiert auf document, damit es Section-Reloads im Theme-Editor übersteht.
 * Klick/Tastatur (DetailsDisclosure) bleibt als Fallback erhalten. */
if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  const HOVER_CLOSE_DELAY = 200;

  document.addEventListener('mouseover', (event) => {
    if (!(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.nav-menu details-disclosure');
    if (!disclosure) return;

    clearTimeout(disclosure.hoverCloseTimer);
    const details = disclosure.querySelector('details');
    if (details && !details.open) details.open = true;
  });

  document.addEventListener('mouseout', (event) => {
    if (!(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.nav-menu details-disclosure');
    if (!disclosure) return;
    if (event.relatedTarget && disclosure.contains(event.relatedTarget)) return;

    clearTimeout(disclosure.hoverCloseTimer);
    disclosure.hoverCloseTimer = setTimeout(() => {
      const details = disclosure.querySelector('details');
      if (details) details.open = false;
    }, HOVER_CLOSE_DELAY);
  });

  /* Mausklick auf einen Menüpunkt mit eigener URL führt direkt zur Seite,
   * das Panel öffnet ohnehin schon per Hover. Tastatur (event.detail === 0)
   * togglet weiterhin das Panel, damit die Bedienung per Enter erhalten bleibt. */
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest('.nav-menu summary[data-url]');
    if (!summary || event.detail === 0) return;

    event.preventDefault();
    window.location.href = summary.dataset.url;
  });
}

/* --------------------------------------------------------------------------
 * Modal
 *
 * <modal-dialog id="..."> mit .modal__overlay[data-modal-close] und
 * .modal__content. Öffnen über <button data-modal-open aria-controls="ID">.
 * ------------------------------------------------------------------------ */

class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-modal-close]')) this.close();
    });

    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  open(opener) {
    this.opener = opener || this.opener;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    const content = this.querySelector('.modal__content');
    if (content) theme.utils.trapFocus(content);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    theme.utils.removeTrapFocus(this.querySelector('.modal__content'));
    this.opener?.focus();
  }
}

if (!customElements.get('modal-dialog')) {
  customElements.define('modal-dialog', ModalDialog);
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-modal-open]');
  if (!opener) return;

  const modal = document.getElementById(opener.getAttribute('aria-controls'));
  if (modal && typeof modal.open === 'function') {
    event.preventDefault();
    modal.open(opener);
  }
});

/* --------------------------------------------------------------------------
 * Newsletter Form (im Newsletter-Modal)
 *
 * Schickt die Anmeldung per Fetch ab und zeigt den Erfolg direkt im Modal,
 * ohne die Seite neu zu laden. Nur eine Antwort mit customer_posted=true
 * gilt als Erfolg; bei jedem anderen Ausgang (Captcha-Seite, Umleitung,
 * Netzwerkfehler) wird klassisch abgeschickt, damit nie eine falsche
 * Fehlermeldung aufblitzt. Nach einem klassischen Absenden (Seite lädt mit
 * customer_posted=true) öffnet sich das Modal mit der Erfolgsmeldung, außer
 * die Seite hat eine eigene Newsletter-Sektion, die sie selbst anzeigt.
 * ------------------------------------------------------------------------ */

class NewsletterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.fields = this.querySelector('[data-newsletter-fields]');
    this.success = this.querySelector('[data-newsletter-success]');
    if (!this.form) return;

    this.form.addEventListener('submit', (event) => this.submit(event));

    if (this.success && !this.success.hidden && !document.getElementById('newsletter')) {
      const modal = this.closest('modal-dialog');
      if (modal && typeof modal.open === 'function') modal.open();
    }
  }

  async submit(event) {
    event.preventDefault();

    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });

      if (response.ok && response.url.includes('customer_posted=true')) {
        if (this.fields) this.fields.hidden = true;
        if (this.success) this.success.hidden = false;
        if (button) button.disabled = false;
        return;
      }

      this.form.submit();
    } catch (fetchError) {
      this.form.submit();
    }
  }
}

if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', NewsletterForm);
}

/* --------------------------------------------------------------------------
 * Quantity Input
 * ------------------------------------------------------------------------ */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const previousValue = this.input.value;

        if (button.name === 'plus') this.input.stepUp();
        if (button.name === 'minus') this.input.stepDown();

        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

/* --------------------------------------------------------------------------
 * Scroll Slider
 * Wischleiste mit Pfeiltasten für Mausbedienung. Die Pfeile erscheinen nur,
 * wenn der Inhalt tatsächlich übersteht, und springen um eine Kachel weiter.
 * ------------------------------------------------------------------------ */

class ScrollSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');

    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);

    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  step(direction) {
    const item = this.track.querySelector(':scope > *');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  update() {
    const overflow = this.track.scrollWidth > this.track.clientWidth + 1;
    this.toggleAttribute('data-overflow', overflow);
    if (!overflow) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 1;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
  }
}

if (!customElements.get('scroll-slider')) {
  customElements.define('scroll-slider', ScrollSlider);
}

/* --------------------------------------------------------------------------
 * Article Table of Contents
 * Baut das Inhaltsverzeichnis eines Ratgeberbeitrags aus den h2-Überschriften
 * des gerenderten Textes. Der Beitragstext aus dem Shopify-Admin bleibt
 * dadurch unverändert und muss keine Anker mitbringen.
 * ------------------------------------------------------------------------ */

class ArticleToc extends HTMLElement {
  connectedCallback() {
    if (this.dataset.built) return;

    const body = this.parentElement?.querySelector('.article__body');
    if (!body) return;

    const headings = Array.from(body.querySelectorAll('h2'));
    if (headings.length < 3) return;

    const list = document.createElement('ol');
    list.className = 'article-toc__list';

    headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `abschnitt-${index + 1}`;

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
    });

    const title = document.createElement('p');
    title.className = 'article-toc__title';
    title.textContent = this.dataset.heading || 'Inhalt';

    this.appendChild(title);
    this.appendChild(list);
    this.dataset.built = 'true';
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}

/* --------------------------------------------------------------------------
 * Shopify Theme Editor
 * Custom Elements initialisieren sich beim Neu-Rendern von Sections selbst.
 * Hier nur Editor-spezifisches Verhalten (z. B. Cart Drawer anzeigen).
 * ------------------------------------------------------------------------ */

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('drawer-component');
    if (drawer) drawer.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = event.target.querySelector('drawer-component');
    if (drawer) drawer.close();
  });
}
